/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CartContext } from "@cart-client/contexts/CartContext";
import { getOnPremiseFulfilmentData, isOnPremiseFulfilmentCart } from "@cart-client/helpers/OnPremiseFulfilment";
import { useOnPremiseFulfilmentAnalytics } from "@cart-client/hooks/useOnPremiseFulfilmentAnalytics";
import { OnPremiseFulfilmentData, CART_JOURNEY_ADDITIONAL_CONTEXT_KEYS } from "@swiggy-private/cart-types";
import {
    CinemaWidget,
    TEXTS,
    OrderTypeBottomSheet,
    ORDER_PREFERENCE,
    type OrderTypeHalfCardAnalyticsHandlers,
} from "@swiggy-private/cart-ui";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

export const OnPremiseWidget = ({
    showOrderTypeSheet,
    setShowOrderTypeSheet,
}: {
    showOrderTypeSheet: boolean;
    setShowOrderTypeSheet: (show: boolean) => void;
}) => {
    const { cartState } = useContext(CartContext);
    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const [premiseTooltipText, setPremiseTooltipText] = useState<string | null>(null);
    const [orderTypeSheetFocusTarget, setOrderTypeSheetFocusTarget] = useState<"order_type" | "mobile" | undefined>(
        undefined,
    );
    const { reviewedCart: cartResponse, localCart } = cartState;

    const onPremiseData = useMemo(() => getOnPremiseFulfilmentData(cartResponse?.data ?? null), [cartResponse]);

    const isOnPremiseFulfilment = useMemo(() => isOnPremiseFulfilmentCart(cartResponse?.data ?? null), [cartResponse]);

    const {
        trackOrderTypeCtaClick,
        trackEditMobileNumber,
        trackOrderTypeHalfCardImpression,
        trackOrderTypeHalfCardDeliver,
        trackOrderTypeHalfCardPickup,
        trackOrderTypeHalfCardMobileNumber,
        trackOrderTypeHalfCardClose,
        trackOrderTypeHalfCardConfirm,
    } = useOnPremiseFulfilmentAnalytics(cartResponse, onPremiseData);

    const dismissPremiseTooltip = useCallback(() => {
        setPremiseTooltipText(null);
    }, []);

    const showPremiseTooltip = useCallback((text: string) => {
        setPremiseTooltipText(text);
    }, []);

    const closeOrderTypeSheet = useCallback(() => {
        trackOrderTypeHalfCardClose();
        setShowOrderTypeSheet(false);
        setOrderTypeSheetFocusTarget(undefined);
    }, [setShowOrderTypeSheet, trackOrderTypeHalfCardClose]);

    const buildOnPremiseContext = useCallback(
        (orderPreference: string, audiNumber?: string, seatNumber?: string, mobileNumber?: string): string => {
            const current = onPremiseData;
            return JSON.stringify({
                is_on_premise_fulfilment_order: current?.is_on_premise_fulfilment_order,
                order_preference: orderPreference,
                audi_number: audiNumber || current?.audi_number || "",
                seat_number: seatNumber || current?.seat_number || "",
                mobile_number: mobileNumber || current?.mobile_number || "",
            });
        },
        [onPremiseData],
    );

    const applyOnPremiseContextToCart = useCallback(
        (orderPreference: string, audiNumber: string, seatNumber: string, mobileNumber: string) => {
            const newContext = buildOnPremiseContext(orderPreference, audiNumber, seatNumber, mobileNumber);
            doUpdateCart({
                ...localCart,
                cart_journey_ctx: {
                    ...localCart?.cart_journey_ctx,
                    additional_context: {
                        ...localCart?.cart_journey_ctx?.additional_context,
                        [CART_JOURNEY_ADDITIONAL_CONTEXT_KEYS.ON_PREMISE_FULFILMENT]: newContext,
                    },
                },
            });
        },
        [buildOnPremiseContext, doUpdateCart, localCart],
    );

    const handleOrderTypeConfirmDeliver = useCallback(
        (audiNumber: string, seatNumber: string, mobileNumber: string) => {
            applyOnPremiseContextToCart(ORDER_PREFERENCE.DELIVER_TO_SEAT, audiNumber, seatNumber, mobileNumber);
            closeOrderTypeSheet();
            showPremiseTooltip(TEXTS.ON_PREMISE_TOOLTIP_SEAT_UPDATED);
        },
        [applyOnPremiseContextToCart, closeOrderTypeSheet, showPremiseTooltip],
    );

    const handleOrderTypeConfirmPickup = useCallback(
        (mobileNumber: string) => {
            applyOnPremiseContextToCart(ORDER_PREFERENCE.PICKUP_AT_COUNTER, "", "", mobileNumber);
            closeOrderTypeSheet();
            showPremiseTooltip(TEXTS.ON_PREMISE_TOOLTIP_ORDER_TYPE_UPDATED);
        },
        [applyOnPremiseContextToCart, closeOrderTypeSheet, showPremiseTooltip],
    );

    const handleOrderTypeEditClick = useCallback(() => {
        trackOrderTypeCtaClick();
        setOrderTypeSheetFocusTarget("order_type");
        setShowOrderTypeSheet(true);
    }, [trackOrderTypeCtaClick, setShowOrderTypeSheet]);

    const handleMobileEditClick = useCallback(() => {
        trackEditMobileNumber();
        setOrderTypeSheetFocusTarget("mobile");
        setShowOrderTypeSheet(true);
    }, [trackEditMobileNumber, setShowOrderTypeSheet]);

    const halfCardAnalytics = useMemo<OrderTypeHalfCardAnalyticsHandlers>(
        () => ({
            onImpression: trackOrderTypeHalfCardImpression,
            onDeliverPreferenceClick: trackOrderTypeHalfCardDeliver,
            onPickupPreferenceClick: trackOrderTypeHalfCardPickup,
            onMobileNumberFocus: trackOrderTypeHalfCardMobileNumber,
            onConfirm: trackOrderTypeHalfCardConfirm,
        }),
        [
            trackOrderTypeHalfCardConfirm,
            trackOrderTypeHalfCardDeliver,
            trackOrderTypeHalfCardImpression,
            trackOrderTypeHalfCardMobileNumber,
            trackOrderTypeHalfCardPickup,
        ],
    );

    useEffect(() => {
        if (premiseTooltipText === null) return;
        const handleScroll = () => {
            dismissPremiseTooltip();
        };
        document.addEventListener("scroll", handleScroll, { capture: true, passive: true });
        return () => {
            document.removeEventListener("scroll", handleScroll, { capture: true });
        };
    }, [premiseTooltipText, dismissPremiseTooltip]);

    if (!isOnPremiseFulfilment || !onPremiseData) return null;

    return (
        <>
            <CinemaWidget
                onPremiseData={onPremiseData as OnPremiseFulfilmentData}
                onOrderTypeEditClick={handleOrderTypeEditClick}
                onMobileEditClick={handleMobileEditClick}
                premiseTooltipText={premiseTooltipText}
                onDismissPremiseTooltip={dismissPremiseTooltip}
            />

            {showOrderTypeSheet && (
                <OrderTypeBottomSheet
                    isOpen={showOrderTypeSheet}
                    currentOrderPreference={onPremiseData?.order_preference ?? ""}
                    currentAudiNumber={onPremiseData?.audi_number ?? ""}
                    currentSeatNumber={onPremiseData?.seat_number ?? ""}
                    currentMobileNumber={onPremiseData?.mobile_number ?? ""}
                    updateCartInProgress={updateCartInProgress}
                    openFocusTarget={orderTypeSheetFocusTarget}
                    analytics={halfCardAnalytics}
                    onClose={closeOrderTypeSheet}
                    onConfirmDeliverToSeat={handleOrderTypeConfirmDeliver}
                    onConfirmPickupAtCounter={handleOrderTypeConfirmPickup}
                />
            )}
        </>
    );
};

export default memo(OnPremiseWidget);
